/**
 * Offering Memorandum Service
 * Handles CRUD operations and SharePoint file management for OMs
 */

import { supabase } from '@/lib/supabase';
import { FOLDER_MARKER_NAME } from '@/lib/omFileSync';
import { uploadFile, createFolderByPath, getDownloadUrl } from '@/services/sharepoint.service';

// =============================================================================
// TYPES
// =============================================================================

export interface OfferingMemorandum {
  id: string;
  file_name: string;
  file_size: number;
  file_type: string | null;
  sharepoint_item_id: string | null;
  sharepoint_url: string | null;
  sharepoint_web_url: string | null;
  sharepoint_drive_id: string | null;
  /** Supabase Storage signed URL — fallback when SharePoint is unavailable */
  file_url: string | null;
  property_type: 'mhc' | 'self_storage';
  property_name: string | null;
  status: 'new' | 'reviewing' | 'pass';
  status_changed_at: string;
  status_changed_by: string | null;
  status_changed_by_name: string | null;
  uploaded_by: string;
  uploaded_by_name: string;
  uploaded_by_email: string | null;
  notes: string | null;
  is_archived: boolean;
  created_at: string;
  updated_at: string;
}

export interface OMInsert {
  file_name: string;
  file_size: number;
  file_type?: string;
  sharepoint_item_id?: string;
  sharepoint_url?: string;
  sharepoint_web_url?: string;
  sharepoint_drive_id?: string;
  property_type: 'mhc' | 'self_storage';
  property_name?: string;
  uploaded_by: string;
  uploaded_by_name: string;
  uploaded_by_email?: string;
  notes?: string;
}

export interface OMFilters {
  status?: string;
  property_type?: string;
  search?: string;
  showArchived?: boolean;
}

export interface OMListResponse {
  data: OfferingMemorandum[];
  count: number;
}

export interface OMPropertyLink {
  id: string;
  offering_memorandum_id: string;
  property_id: string;
  linked_at: string;
  linked_by: string | null;
}

export interface LinkedProperty {
  id: string;
  name: string;
  address_city: string | null;
  address_state: string | null;
  source: 'properties';
}

export interface PropertyLinkInput {
  id: string;
  source?: 'properties'; // Unified table — always 'properties'
}

// =============================================================================
// OM FILE TYPES (per-category file management)
// =============================================================================

export type OMFileCategory = 'om' | 'nda' | 'project_files';

export interface OMFile {
  id: string;
  offering_memorandum_id: string;
  category: OMFileCategory;
  file_name: string;
  file_size: number;
  file_type: string | null;
  relative_path: string | null;
  sharepoint_item_id: string | null;
  sharepoint_url: string | null;
  sharepoint_web_url: string | null;
  sharepoint_drive_id: string | null;
  uploaded_by: string;
  uploaded_by_name: string;
  uploaded_by_email: string | null;
  is_offering_memorandum: boolean;
  /** Set when the SharePoint upload threw; the row is kept so it can be retried. */
  upload_failed: boolean;
  upload_error: string | null;
  created_at: string;
  updated_at: string;
}

export interface OMFileInsert {
  offering_memorandum_id: string;
  category: OMFileCategory;
  file_name: string;
  file_size: number;
  file_type?: string;
  relative_path?: string;
  uploaded_by: string;
  uploaded_by_name: string;
  uploaded_by_email?: string;
}

export interface OMFileCounts {
  om: number;
  nda: number;
  project_files: number;
}

const OM_FILES_TABLE = 'offering_memorandum_files';

const CATEGORY_FOLDER_NAMES: Record<OMFileCategory, string> = {
  om: 'OM',
  nda: 'NDA',
  project_files: 'Project Files',
};

// SharePoint folder for all OMs
const OM_SHAREPOINT_FOLDER = '/Offering Memorandums';

// =============================================================================
// QUERY OPERATIONS
// =============================================================================

/**
 * Get offering memorandums with optional filters and pagination
 */
export async function getOfferingMemorandums(
  filters?: OMFilters,
  page = 1,
  pageSize = 25
): Promise<OMListResponse> {
  const from = (page - 1) * pageSize;
  const to = from + pageSize - 1;

  let query = supabase
    .from('offering_memorandums')
    .select('*', { count: 'exact' })
    .order('created_at', { ascending: false })
    .range(from, to);

  // Filter by archived status (default: hide archived)
  if (!filters?.showArchived) {
    query = query.eq('is_archived', false);
  }

  if (filters?.status && filters.status !== 'all') {
    query = query.eq('status', filters.status);
  }

  if (filters?.property_type && filters.property_type !== 'all') {
    query = query.eq('property_type', filters.property_type);
  }

  if (filters?.search) {
    query = query.or(
      `file_name.ilike.%${filters.search}%,property_name.ilike.%${filters.search}%,uploaded_by_name.ilike.%${filters.search}%`
    );
  }

  const { data, error, count } = await query;

  if (error) {
    console.error('Error fetching offering memorandums:', error);
    throw new Error(error.message);
  }

  return {
    data: data || [],
    count: count || 0,
  };
}

/**
 * Get a single offering memorandum by ID
 */
export async function getOfferingMemorandumById(
  id: string
): Promise<OfferingMemorandum | null> {
  const { data, error } = await supabase
    .from('offering_memorandums')
    .select('*')
    .eq('id', id)
    .single();

  if (error) {
    console.error('Error fetching offering memorandum:', error);
    throw new Error(error.message);
  }

  return data;
}

/**
 * Get counts by status (excludes archived by default)
 */
export async function getOMCountsByStatus(): Promise<Record<string, number>> {
  const { data, error } = await supabase
    .from('offering_memorandums')
    .select('status, is_archived');

  if (error) {
    console.error('Error fetching OM counts:', error);
    return {};
  }

  const counts: Record<string, number> = { new: 0, reviewing: 0, pass: 0, archived: 0 };
  (data || []).forEach((item: { status: string; is_archived?: boolean }) => {
    if (item.is_archived) {
      counts.archived = (counts.archived || 0) + 1;
    } else {
      counts[item.status] = (counts[item.status] || 0) + 1;
    }
  });

  return counts;
}

/**
 * Archive a prospect (soft delete)
 */
export async function archiveOM(id: string): Promise<void> {
  const { error } = await supabase
    .from('offering_memorandums')
    .update({ is_archived: true })
    .eq('id', id);

  if (error) {
    console.error('Error archiving OM:', error);
    throw new Error(error.message);
  }
}

/**
 * Unarchive (restore) a prospect
 */
export async function unarchiveOM(id: string): Promise<void> {
  const { error } = await supabase
    .from('offering_memorandums')
    .update({ is_archived: false })
    .eq('id', id);

  if (error) {
    console.error('Error unarchiving OM:', error);
    throw new Error(error.message);
  }
}

// =============================================================================
// MUTATION OPERATIONS
// =============================================================================

/**
 * Create a new prospect (offering memorandum) without uploading a file.
 * Use this when creating a prospect first, then attaching files separately.
 */
export async function createProspect(
  propertyType: 'mhc' | 'self_storage',
  userId: string,
  userName: string,
  userEmail: string,
  propertyName?: string,
  notes?: string
): Promise<OfferingMemorandum> {
  const insertData: OMInsert = {
    file_name: propertyName || 'New Prospect',
    file_size: 0,
    property_type: propertyType,
    property_name: propertyName || undefined,
    uploaded_by: userId,
    uploaded_by_name: userName,
    uploaded_by_email: userEmail,
    notes: notes || undefined,
  };

  const { data, error } = await supabase
    .from('offering_memorandums')
    .insert(insertData)
    .select()
    .single();

  if (error) {
    console.error('Error creating prospect:', error);
    throw new Error(error.message);
  }

  return data;
}

/**
 * Update an offering memorandum's status
 */
export async function updateOMStatus(
  id: string,
  status: 'new' | 'reviewing' | 'pass',
  userId: string,
  userName: string
): Promise<OfferingMemorandum> {
  const { data, error } = await supabase
    .from('offering_memorandums')
    .update({
      status,
      status_changed_at: new Date().toISOString(),
      status_changed_by: userId,
      status_changed_by_name: userName,
    })
    .eq('id', id)
    .select()
    .single();

  if (error) {
    console.error('Error updating OM status:', error);
    throw new Error(error.message);
  }

  return data;
}

/**
 * Delete an offering memorandum
 */
export async function deleteOfferingMemorandum(id: string): Promise<void> {
  const { error } = await supabase
    .from('offering_memorandums')
    .delete()
    .eq('id', id);

  if (error) {
    console.error('Error deleting offering memorandum:', error);
    throw new Error(error.message);
  }
}

/**
 * Fetch a pre-authenticated temporary download URL from Graph API for an OM.
 * The `@microsoft.graph.downloadUrl` is a CDN URL that works in an iframe without auth headers.
 * Automatically saves the URL to file_url for future use.
 */
export async function fetchAndCacheOMPreviewUrl(om: OfferingMemorandum): Promise<string> {
  if (!om.sharepoint_item_id) {
    throw new Error('No SharePoint item ID available');
  }

  const downloadUrl = await getDownloadUrl(om.sharepoint_item_id);

  // Cache to file_url so we don't call Graph API every time
  await supabase
    .from('offering_memorandums')
    .update({ file_url: downloadUrl })
    .eq('id', om.id);

  return downloadUrl;
}

// =============================================================================
// OM FILE OPERATIONS (per-category)
// =============================================================================

/**
 * Sanitize a property name for use as a SharePoint folder name.
 * Removes characters that are invalid in SharePoint paths.
 */
function sanitizeFolderName(name: string): string {
  return name
    .replace(/[#%*:<>?/\\|"]/g, '')
    .replace(/\s+/g, ' ')
    .trim()
    .slice(0, 100); // Keep it reasonable
}

/**
 * Get the SharePoint folder path for an OM's category files.
 * Structure: /Offering Memorandums/{property_name}/{Category}/
 */
function getOMCategoryPath(
  om: OfferingMemorandum,
  category: OMFileCategory,
  relativePath?: string
): string {
  const folderName = om.property_name
    ? sanitizeFolderName(om.property_name)
    : om.id;
  const categoryFolder = CATEGORY_FOLDER_NAMES[category];
  let path = `${OM_SHAREPOINT_FOLDER}/${folderName}/${categoryFolder}`;
  if (relativePath) {
    path += `/${relativePath}`;
  }
  return path;
}

/**
 * Get all files for an offering, optionally filtered by category
 */
export async function getOMFiles(
  omId: string,
  category?: OMFileCategory
): Promise<OMFile[]> {
  let query = supabase
    .from(OM_FILES_TABLE)
    .select('*')
    .eq('offering_memorandum_id', omId)
    .order('relative_path', { ascending: true, nullsFirst: true })
    .order('file_name', { ascending: true });

  if (category) {
    query = query.eq('category', category);
  }

  const { data, error } = await query;

  if (error) {
    console.error('Error fetching OM files:', error);
    throw new Error(error.message);
  }

  return data || [];
}

/**
 * Get file counts per category for an OM
 */
export async function getOMFileCounts(omId: string): Promise<OMFileCounts> {
  const { data, error } = await supabase
    .from(OM_FILES_TABLE)
    .select('category')
    .eq('offering_memorandum_id', omId);

  if (error) {
    console.error('Error fetching OM file counts:', error);
    return { om: 0, nda: 0, project_files: 0 };
  }

  const counts: OMFileCounts = { om: 0, nda: 0, project_files: 0 };
  (data || []).forEach((row: { category: OMFileCategory }) => {
    counts[row.category] = (counts[row.category] || 0) + 1;
  });

  return counts;
}

/** Trim an arbitrary thrown value down to something that fits in upload_error. */
function describeUploadError(err: unknown): string {
  const message = err instanceof Error && err.message ? err.message : String(err);
  return message.slice(0, 500);
}

/**
 * Flag a row whose SharePoint upload failed, so the file browser shows it as
 * failed-and-retryable instead of as an ordinary file.
 *
 * Best effort by design: this runs on a path where the network has already let
 * us down once. If the flag write also fails the row still has no
 * sharepoint_item_id, which omFileSyncState resolves to 'failed' on its own
 * once the stale window passes — the flag sharpens that verdict and carries the
 * reason, it isn't the only thing holding it up.
 */
async function markOMFileUploadFailed(fileId: string, err: unknown): Promise<void> {
  const { error } = await supabase
    .from(OM_FILES_TABLE)
    .update({
      upload_failed: true,
      upload_error: describeUploadError(err),
      updated_at: new Date().toISOString(),
    })
    .eq('id', fileId);

  if (error) {
    console.error('[OM File Upload] Could not flag the failed row:', error);
  }
}

/**
 * Upload a file to a specific category for an OM.
 * Creates the DB row first, then uploads to SharePoint and updates the row with metadata.
 *
 * The row is written first so a failed upload still leaves a record of what the
 * user meant to add — for a folder upload that record is the only surviving
 * list of which files didn't make it. Everything after the insert therefore
 * runs under a catch that flags the row before rethrowing, so the file browser
 * can offer a retry rather than list a file whose download can only fail.
 */
export async function uploadOMFile(
  file: File,
  omId: string,
  category: OMFileCategory,
  userId: string,
  userName: string,
  userEmail: string,
  relativePath?: string
): Promise<OMFile> {
  const fileType = file.name.split('.').pop()?.toLowerCase() || null;
  const isFolderMarker = file.name === FOLDER_MARKER_NAME;

  // 1. Create DB record
  const insertData: OMFileInsert = {
    offering_memorandum_id: omId,
    category,
    file_name: file.name,
    file_size: file.size,
    file_type: fileType || undefined,
    relative_path: relativePath || undefined,
    uploaded_by: userId,
    uploaded_by_name: userName,
    uploaded_by_email: userEmail,
  };

  const { data, error } = await supabase
    .from(OM_FILES_TABLE)
    .insert(insertData)
    .select()
    .single();

  if (error) {
    console.error('Error creating OM file record:', error);
    throw new Error(error.message);
  }

  // Skip SharePoint upload for folder markers - just create DB record
  if (isFolderMarker) {
    return data;
  }

  try {
    // 2. Get parent OM for folder path
    const om = await getOfferingMemorandumById(omId);
    if (!om) {
      throw new Error('Parent offering memorandum not found');
    }

    // 3. Upload to SharePoint
    const folderPath = getOMCategoryPath(om, category, relativePath);

    try {
      await createFolderByPath(folderPath);
    } catch {
      // The folder may already exist
    }

    const sharePointResult = await uploadFile(file, folderPath);

    // No id and no URL means we have nothing to download the file by later,
    // which is a failed upload however the call returned.
    if (!sharePointResult?.id && !sharePointResult?.webUrl) {
      throw new Error('SharePoint accepted the upload but returned no item id or URL');
    }

    const { error: updateError } = await supabase
      .from(OM_FILES_TABLE)
      .update({
        sharepoint_item_id: sharePointResult.id || null,
        sharepoint_url: sharePointResult.webUrl || null,
        sharepoint_web_url: sharePointResult.webUrl || null,
        updated_at: new Date().toISOString(),
      })
      .eq('id', data.id);

    if (updateError) {
      // The bytes are in SharePoint but the row can't point at them, so the
      // file is just as unreachable as if the upload had failed. A retry
      // re-uploads to the same path, which is safe.
      throw new Error(
        `SharePoint upload succeeded but the file record could not be updated: ${updateError.message}`
      );
    }

    data.sharepoint_item_id = sharePointResult.id || null;
    data.sharepoint_url = sharePointResult.webUrl || null;
    data.sharepoint_web_url = sharePointResult.webUrl || null;
  } catch (uploadError) {
    console.error('[OM File Upload] SharePoint upload failed:', uploadError);
    await markOMFileUploadFailed(data.id, uploadError);
    data.upload_failed = true;
    data.upload_error = describeUploadError(uploadError);
    throw uploadError;
  }

  return data;
}

/**
 * Rename an OM file (updates database record only, SharePoint name unchanged)
 */
export async function renameOMFile(fileId: string, newFileName: string): Promise<OMFile> {
  const trimmedName = newFileName.trim();
  if (!trimmedName) {
    throw new Error('File name cannot be empty');
  }

  const { data, error } = await supabase
    .from(OM_FILES_TABLE)
    .update({
      file_name: trimmedName,
      updated_at: new Date().toISOString(),
    })
    .eq('id', fileId)
    .select()
    .single();

  if (error) {
    console.error('Error renaming OM file:', error);
    throw new Error(error.message);
  }

  return data;
}

/**
 * Toggle the is_offering_memorandum flag on a file
 * Allows marking/unmarking files as official Offering Memorandum documents
 */
export async function toggleOMFileFlag(fileId: string, isOM: boolean): Promise<OMFile> {
  const { data, error } = await supabase
    .from(OM_FILES_TABLE)
    .update({
      is_offering_memorandum: isOM,
      updated_at: new Date().toISOString(),
    })
    .eq('id', fileId)
    .select()
    .single();

  if (error) {
    console.error('Error toggling OM flag:', error);
    throw new Error(error.message);
  }

  return data;
}

/**
 * Delete an OM file record from the database.
 * Does not delete from SharePoint (SharePoint is the source of truth for storage).
 */
export async function deleteOMFile(fileId: string): Promise<void> {
  const { error } = await supabase
    .from(OM_FILES_TABLE)
    .delete()
    .eq('id', fileId);

  if (error) {
    console.error('Error deleting OM file:', error);
    throw new Error(error.message);
  }
}

/**
 * Get a download URL for an OM file from SharePoint
 */
export async function getOMFileDownloadUrl(file: OMFile): Promise<string> {
  if (!file.sharepoint_item_id) {
    throw new Error('No SharePoint item ID available for this file');
  }
  return getDownloadUrl(file.sharepoint_item_id);
}

/**
 * Retry SharePoint sync for an OM file that failed to upload.
 * This re-uploads the file to SharePoint and updates the database record.
 *
 * The caller must supply the file content: the row records what the file was,
 * never its bytes, so a retry means the user re-picking it from disk. What the
 * row buys is everything else — the category, the folder it belonged in, the
 * OM flag and the original uploader all survive, so the retry lands where the
 * first attempt was aimed instead of wherever the user drops it.
 */
export async function retrySyncOMFile(
  file: OMFile,
  fileContent: File,
  omId: string
): Promise<OMFile> {
  try {
    const om = await getOfferingMemorandumById(omId);
    if (!om) {
      throw new Error('Parent offering memorandum not found');
    }

    const folderPath = getOMCategoryPath(om, file.category, file.relative_path || undefined);

    try {
      await createFolderByPath(folderPath);
    } catch {
      // The folder may already exist
    }

    const sharePointResult = await uploadFile(fileContent, folderPath);

    if (!sharePointResult?.id) {
      throw new Error('SharePoint upload failed - no item ID returned');
    }

    const { data, error } = await supabase
      .from(OM_FILES_TABLE)
      .update({
        sharepoint_item_id: sharePointResult.id,
        sharepoint_url: sharePointResult.webUrl || null,
        sharepoint_web_url: sharePointResult.webUrl || null,
        upload_failed: false,
        upload_error: null,
        updated_at: new Date().toISOString(),
      })
      .eq('id', file.id)
      .select()
      .single();

    if (error) {
      throw new Error(error.message);
    }

    return data;
  } catch (retryError) {
    console.error('[Retry Sync] SharePoint sync failed:', retryError);
    await markOMFileUploadFailed(file.id, retryError);
    throw retryError;
  }
}

// =============================================================================
// OM–PROPERTY LINKING
// =============================================================================

/**
 * Get all properties linked to an OM (unified properties table)
 */
export async function getLinkedProperties(omId: string): Promise<LinkedProperty[]> {
  const { data: links, error } = await supabase
    .from('offering_memorandum_properties')
    .select('property_id')
    .eq('offering_memorandum_id', omId);

  if (error) {
    console.error('Error fetching linked properties:', error);
    throw new Error(error.message);
  }

  if (!links || links.length === 0) return [];

  const propertyIds = links.map((l) => l.property_id);

  const { data: props } = await supabase
    .from('properties')
    .select('id, name, address_city, address_state')
    .in('id', propertyIds);

  if (!props) return [];
  return props.map((p) => ({ ...p, source: 'properties' as const }));
}

/**
 * Get all OMs linked to a property
 */
export async function getPropertyOMs(propertyId: string): Promise<OfferingMemorandum[]> {
  const { data, error } = await supabase
    .from('offering_memorandum_properties')
    .select(`
      offering_memorandums:offering_memorandum_id (*)
    `)
    .eq('property_id', propertyId)
    // The FK lives on offering_memorandum_properties, so this is a to-one embed:
    // each row carries a single OM object (supabase-js can't infer this without generated DB types)
    .returns<{ offering_memorandums: OfferingMemorandum }[]>();

  if (error) {
    console.error('Error fetching property OMs:', error);
    throw new Error(error.message);
  }

  return (data || []).map((row) => row.offering_memorandums);
}

/**
 * Link an OM to one or more properties (unified table)
 */
export async function linkOMToProperties(
  omId: string,
  propertyLinks: PropertyLinkInput[],
  linkedBy?: string
): Promise<void> {
  const inserts = propertyLinks.map((link) => ({
    offering_memorandum_id: omId,
    property_id: link.id,
    linked_by: linkedBy || null,
  }));

  const { error } = await supabase
    .from('offering_memorandum_properties')
    .upsert(inserts, { onConflict: 'offering_memorandum_id,property_id' });

  if (error) {
    console.error('Error linking OM to properties:', error);
    throw new Error(error.message);
  }
}

/**
 * Replace all property links for an OM
 */
export async function setOMPropertyLinks(
  omId: string,
  propertyLinks: PropertyLinkInput[],
  linkedBy?: string
): Promise<void> {
  // Delete existing links
  const { error: deleteError } = await supabase
    .from('offering_memorandum_properties')
    .delete()
    .eq('offering_memorandum_id', omId);

  if (deleteError) {
    console.error('Error clearing OM property links:', deleteError);
    throw new Error(deleteError.message);
  }

  // Insert new links
  if (propertyLinks.length > 0) {
    await linkOMToProperties(omId, propertyLinks, linkedBy);
  }
}

// =============================================================================
// EMAIL NOTIFICATIONS
// =============================================================================

// Notification recipient for OM uploads
const OM_NOTIFICATION_EMAIL = 'ryan@elevationcg.com';

interface OMUploadSummary {
  fileName: string;
  fileSize: number;
  propertyType: 'mhc' | 'self_storage';
  propertyName?: string;
  status: 'completed' | 'failed';
  error?: string;
}

/**
 * Send email notification after OM upload batch completes.
 * Uses Microsoft Graph API to send from the authenticated user's mailbox.
 */
export async function sendOMUploadNotification(
  uploads: OMUploadSummary[],
  uploaderName: string,
  uploaderEmail: string
): Promise<void> {
  // Only send if there are successful uploads
  const successful = uploads.filter((u) => u.status === 'completed');
  if (successful.length === 0) return;

  const failed = uploads.filter((u) => u.status === 'failed');

  // Import dynamically to avoid circular deps
  const { getMsalInstance } = await import('@/providers/MsalProvider');
  const { graphScopes } = await import('@/lib/msalConfig');

  const msalInstance = getMsalInstance();
  if (!msalInstance) {
    console.warn('[OM Notification] MSAL not configured, skipping email');
    return;
  }

  const accounts = msalInstance.getAllAccounts();
  if (accounts.length === 0) {
    console.warn('[OM Notification] No authenticated user, skipping email');
    return;
  }

  // Get mail token
  let accessToken: string;
  try {
    const result = await msalInstance.acquireTokenSilent({
      scopes: graphScopes.mailSend,
      account: accounts[0],
    });
    accessToken = result.accessToken;
  } catch {
    console.warn('[OM Notification] Could not acquire mail token, skipping email');
    return;
  }

  // Build email body
  const propertyTypeLabel = (t: string) => t === 'mhc' ? 'MHC' : 'Self Storage';
  const formatSize = (bytes: number) => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
  };

  const fileRows = successful
    .map(
      (u) =>
        `<tr>
          <td style="padding:8px 12px;border-bottom:1px solid #eee;">${u.fileName}</td>
          <td style="padding:8px 12px;border-bottom:1px solid #eee;">${formatSize(u.fileSize)}</td>
          <td style="padding:8px 12px;border-bottom:1px solid #eee;">${propertyTypeLabel(u.propertyType)}</td>
          <td style="padding:8px 12px;border-bottom:1px solid #eee;">${u.propertyName || '—'}</td>
        </tr>`
    )
    .join('');

  const failedSection =
    failed.length > 0
      ? `<div style="margin-top:16px;padding:12px;background:#fef2f2;border:1px solid #fecaca;border-radius:8px;">
          <strong style="color:#dc2626;">${failed.length} file(s) failed to upload:</strong>
          <ul style="margin:8px 0 0;padding-left:20px;">
            ${failed.map((u) => `<li>${u.fileName} — ${u.error || 'Unknown error'}</li>`).join('')}
          </ul>
        </div>`
      : '';

  const subject =
    successful.length === 1
      ? `New OM Uploaded: ${successful[0].fileName}`
      : `${successful.length} New OMs Uploaded`;

  const htmlBody = `
    <div style="font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,sans-serif;max-width:600px;margin:0 auto;">
      <div style="background:#1e3a5f;color:white;padding:20px 24px;border-radius:8px 8px 0 0;">
        <h2 style="margin:0;font-size:18px;">📄 Offering Memorandum${successful.length > 1 ? 's' : ''} Uploaded</h2>
      </div>
      <div style="background:white;padding:20px 24px;border:1px solid #e5e7eb;border-top:none;border-radius:0 0 8px 8px;">
        <p style="margin:0 0 16px;color:#374151;">
          <strong>${uploaderName}</strong> (${uploaderEmail}) uploaded ${successful.length} OM${successful.length > 1 ? 's' : ''} to Flow.
        </p>
        <table style="width:100%;border-collapse:collapse;font-size:14px;">
          <thead>
            <tr style="background:#f9fafb;">
              <th style="padding:8px 12px;text-align:left;border-bottom:2px solid #e5e7eb;color:#6b7280;font-weight:600;">File</th>
              <th style="padding:8px 12px;text-align:left;border-bottom:2px solid #e5e7eb;color:#6b7280;font-weight:600;">Size</th>
              <th style="padding:8px 12px;text-align:left;border-bottom:2px solid #e5e7eb;color:#6b7280;font-weight:600;">Type</th>
              <th style="padding:8px 12px;text-align:left;border-bottom:2px solid #e5e7eb;color:#6b7280;font-weight:600;">Property</th>
            </tr>
          </thead>
          <tbody>${fileRows}</tbody>
        </table>
        ${failedSection}
        <div style="margin-top:20px;text-align:center;">
          <a href="https://flow.elevationcapitalgroup.com/pipeline/inbound-leads" 
             style="display:inline-block;background:#1e3a5f;color:white;padding:10px 24px;border-radius:6px;text-decoration:none;font-weight:500;">
            View in Flow
          </a>
        </div>
        <p style="margin:16px 0 0;font-size:12px;color:#9ca3af;text-align:center;">
          Sent from Flow — Elevation Capital Group
        </p>
      </div>
    </div>
  `;

  // Send via Microsoft Graph
  try {
    const response = await fetch('https://graph.microsoft.com/v1.0/me/sendMail', {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${accessToken}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        message: {
          subject,
          body: {
            contentType: 'HTML',
            content: htmlBody,
          },
          toRecipients: [
            {
              emailAddress: {
                address: OM_NOTIFICATION_EMAIL,
              },
            },
          ],
        },
      }),
    });

    if (!response.ok) {
      const errorBody = await response.text();
      console.error('[OM Notification] Failed to send email:', response.status, errorBody);
    }
  } catch (error) {
    console.error('[OM Notification] Error sending email:', error);
  }
}
